/**
 * Deep Link Helper for K9 Interior Website
 * Handles app detection and smart redirects
 */

export class DeepLinkHelper {
  // Configuration
  static APP_SCHEME = "k9interior";
  static WEB_DOMAIN = window.location.hostname;

  /**
   * Generate app deep link for a product
   * @param {number} productId
   * @returns {string} App deep link
   */
  static generateAppLink(productId) {
    return `${this.APP_SCHEME}://product/${productId}`;
  }

  /**
   * Generate shareable web link for a product
   * @param {number} productId
   * @param {string} productName
   * @returns {string} Web link
   */
  static generateWebLink(productId, productName) {
    const encodedName = encodeURIComponent(productName);
    return `${window.location.origin}/product-details/${productId}?name=${encodedName}`;
  }

  /**
   * Detect if user is on mobile device
   * @returns {boolean}
   */
  static isMobileDevice() {
    const userAgent = navigator.userAgent.toLowerCase();
    return /android|iphone|ipad|ipod|mobile/.test(userAgent);
  }

  /**
   * Detect if user is on iOS
   * @returns {boolean}
   */
  static isIOS() {
    const userAgent = navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod/.test(userAgent);
  }

  /**
   * Detect if user is on Android
   * @returns {boolean}
   */
  static isAndroid() {
    const userAgent = navigator.userAgent.toLowerCase();
    return /android/.test(userAgent);
  }

  /**
   * Try to open product in app
   * @param {number} productId
   * @param {function} onFallback - Callback if app doesn't open
   */
  static tryOpenInApp(productId, onFallback) {
    console.log("🔗 [DEEP_LINK] Attempting to open in app...", productId);

    if (!this.isMobileDevice()) {
      console.log("🔗 [DEEP_LINK] Desktop device detected, skipping app open");
      return;
    }

    const appLink = this.generateAppLink(productId);
    let appOpened = false;

    // Try to open the app
    window.location.href = appLink;
    console.log("🔗 [DEEP_LINK] App link:", appLink);

    // Set up timeout to check if app opened
    const timeout = setTimeout(() => {
      if (!appOpened) {
        console.log("🔗 [DEEP_LINK] App not installed or failed to open");
        if (onFallback) {
          onFallback();
        }
      }
    }, 2500);

    // Listen for visibility change (app opened)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        appOpened = true;
        clearTimeout(timeout);
        console.log("✅ [DEEP_LINK] App opened successfully");
      }
    };

    // Listen for blur event (app opened)
    const handleBlur = () => {
      appOpened = true;
      clearTimeout(timeout);
      console.log("✅ [DEEP_LINK] App opened successfully (blur)");
    };

    document.addEventListener("visibilitychange", handleVisibilityChange, {
      once: true,
    });
    window.addEventListener("blur", handleBlur, { once: true });

    // Cleanup after timeout
    setTimeout(() => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlur);
    }, 3000);
  }

  /**
   * Auto-open app on page load (for shared links)
   * @param {number} productId
   * @param {boolean} autoOpen - Whether to auto-open (default: true on first visit)
   */
  static autoOpenApp(productId, autoOpen = true) {
    if (!this.isMobileDevice()) {
      return;
    }

    // Check if we've already tried to open the app in this session
    const sessionKey = `app_open_attempted_${productId}`;
    const alreadyAttempted = sessionStorage.getItem(sessionKey);

    if (!autoOpen || alreadyAttempted) {
      console.log(
        "🔗 [DEEP_LINK] Skipping auto-open (already attempted or disabled)"
      );
      return;
    }

    // Mark as attempted
    sessionStorage.setItem(sessionKey, "true");

    // Delay slightly to let page load
    setTimeout(() => {
      this.tryOpenInApp(productId);
    }, 1000);
  }

  /**
   * Get app store links
   * @returns {object} App store URLs
   */
  static getAppStoreLinks() {
    return {
      android:
        "https://play.google.com/store/apps/details?id=com.k9interior.app",
      ios: "https://apps.apple.com/app/k9-interior/id123456789",
    };
  }

  /**
   * Open appropriate app store
   */
  static openAppStore() {
    const links = this.getAppStoreLinks();

    if (this.isAndroid()) {
      window.open(links.android, "_blank");
    } else if (this.isIOS()) {
      window.open(links.ios, "_blank");
    } else {
      // Default to Android
      window.open(links.android, "_blank");
    }
  }

  /**
   * Share product (uses Web Share API if available)
   * @param {number} productId
   * @param {string} productName
   * @param {number} price
   */
  static async shareProduct(productId, productName, price) {
    const shareLink = this.generateWebLink(productId, productName);
    const shareData = {
      title: productName,
      text: `Check out ${productName} on K9 Interior!\n₹${price}\n`,
      url: shareLink,
    };

    console.log("📤 [SHARE] Sharing product:", shareData);

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        console.log("✅ [SHARE] Shared successfully via Web Share API");
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("❌ [SHARE] Error sharing:", err);
          this.fallbackShare(shareLink);
        }
      }
    } else {
      this.fallbackShare(shareLink);
    }
  }

  /**
   * Fallback share method (copy to clipboard)
   * @param {string} link
   */
  static fallbackShare(link) {
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(link)
        .then(() => {
          alert("Link copied to clipboard!");
          console.log("✅ [SHARE] Link copied to clipboard");
        })
        .catch((err) => {
          console.error("❌ [SHARE] Failed to copy:", err);
          this.showShareDialog(link);
        });
    } else {
      this.showShareDialog(link);
    }
  }

  /**
   * Show share dialog with link
   * @param {string} link
   */
  static showShareDialog(link) {
    prompt("Copy this link to share:", link);
  }
}

export default DeepLinkHelper;
